import React, { useState, useEffect } from 'react';
import { X, Save, CheckCircle, XCircle } from 'lucide-react';
import { rbacService } from '../../../backend/services/rbac';
import type { UserWithRoles, Role } from '../../../shared/types/auth';
import { toast } from 'sonner';

interface UserProfileModalProps {
  user: UserWithRoles | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
  roles: Role[];
  mode?: 'edit'; // Only edit mode supported for now
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
  user,
  isOpen,
  onClose,
  onUserUpdated,
  roles
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  useEffect(() => {
    console.debug('UserProfileModal: User:', user, 'Roles:', roles.length);
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email
      });
      setSelectedRoles(user.roles.map(role => role.id));
    }
  }, [user, roles]);

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Update user profile
      await rbacService.updateUserProfile(user.id, {
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      // Update roles
      const currentRoleIds = user.roles.map(role => role.id);
      const rolesToAdd = selectedRoles.filter(roleId => !currentRoleIds.includes(roleId));
      const rolesToRemove = currentRoleIds.filter(roleId => !selectedRoles.includes(roleId));

      // Add new roles
      for (const roleId of rolesToAdd) {
        await rbacService.assignRoleToUser(user.id, roleId);
      }

      // Remove old roles
      for (const roleId of rolesToRemove) {
        await rbacService.removeRoleFromUser(user.id, roleId);
      }

      toast.success('User updated successfully');
      onUserUpdated();
      onClose();
    } catch (err) {
      toast.error('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleId: string) => {
    // Find the role name to check if it's super
    const role = roles.find(r => r.id === roleId);
    const isSuperRole = role?.name === 'super';
    const isCurrentUserSuper = user?.roles.some(r => r.name === 'super');
    
    // Prevent any super role changes - super admin can only be assigned on backend
    if (isSuperRole) {
      toast.error('Super admin role can only be assigned by backend administrators');
      return;
    }
    
    // Prevent removing super role from super admin users
    if (isSuperRole && isCurrentUserSuper && selectedRoles.includes(roleId)) {
      toast.error('Cannot remove super admin role from super admin user');
      return;
    }
    
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'super': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'user': return 'bg-green-100 text-green-800 border-green-200';
      case 'client': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isOpen || !user) return null;

  const handleClose = () => {
    setFormData({ first_name: '', last_name: '', email: '' });
    setSelectedRoles([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">Edit User Profile</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* User Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 h-16 w-16">
                  <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-xl font-medium text-gray-700">
                      {user.first_name?.[0] || user.email[0].toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-gray-900">
                    {user.first_name && user.last_name 
                      ? `${user.first_name} ${user.last_name}`
                      : 'No name set'
                    }
                  </h4>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  <div className="flex items-center mt-1">
                    {user.email_confirmed_at ? (
                      <CheckCircle className="h-4 w-4 text-green-500 mr-1" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500 mr-1" />
                    )}
                    <span className="text-xs text-gray-500">
                      {user.email_confirmed_at ? 'Email confirmed' : 'Email not confirmed'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Email address cannot be changed. Contact system administrator if needed.
              </p>
            </div>

            {/* Role Assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Roles
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {roles.map((role) => {
                  const isSuperRole = role.name === 'super';
                  const isSuperRoleDisabled = isSuperRole; // Always disable super role editing
                  
                  return (
                    <label
                      key={role.id}
                      className={`flex items-center p-3 rounded-lg border ${isSuperRoleDisabled ? 'cursor-not-allowed bg-gray-50' : 'cursor-pointer hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(role.id)}
                        onChange={() => handleRoleToggle(role.id)}
                        disabled={isSuperRoleDisabled}
                        className={`rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3 ${isSuperRoleDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(role.name)}`}>
                            {role.name}
                          </span>
                          {isSuperRoleDisabled && (
                            <span className="text-xs text-amber-600 font-medium">(Backend Only)</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                        {isSuperRole && (
                          <p className="text-xs text-amber-600 mt-1 font-medium">
                            ⚠️ Super admin role can only be assigned by backend administrators
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* User Stats - Only show in edit mode */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Account Information</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Created:</span>
                  <span className="ml-2 text-gray-900">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Last Active:</span>
                  <span className="ml-2 text-gray-900">
                    {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;
